import React, { useMemo, useState } from 'react';
import type { SubscriptionTimelineModel, TimelineSegment } from '../../../../utils/subscriptionTimeline';
import {
  formatSegmentRange,
  msToPct,
  timelineMonthTicks,
} from '../../../../utils/subscriptionTimeline';

interface Props {
  model: SubscriptionTimelineModel;
}

const ROW_H = 40;
const LABEL_W = 220;
const CHART_MAX_H = 520;
const AXIS_H = 28;
const TIMELINE_MIN_W = 480;

const KIND_STYLES: Record<
  TimelineSegment['kind'],
  { bar: string; label: string }
> = {
  coverage: {
    bar: 'bg-blue-500/90 hover:bg-blue-600',
    label: 'Paid coverage',
  },
  contract: {
    bar: 'bg-slate-400/80 hover:bg-slate-500',
    label: 'Contract (est.)',
  },
};

function truncateName(name: string, max = 28): string {
  if (name.length <= max) return name;
  return `${name.slice(0, max - 1)}…`;
}

interface TooltipState {
  seg: TimelineSegment;
  subName: string;
  x: number;
  y: number;
}

export const SubscriptionPeriodGanttChart: React.FC<Props> = ({ model }) => {
  const { rows, rangeStart, rangeEndExclusive } = model;
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const monthTicks = useMemo(
    () => timelineMonthTicks(rangeStart, rangeEndExclusive),
    [rangeStart, rangeEndExclusive],
  );

  const todayPct = useMemo(() => {
    const now = new Date();
    now.setHours(12, 0, 0, 0);
    const t = now.getTime();
    if (t < rangeStart.getTime() || t >= rangeEndExclusive.getTime()) return null;
    return msToPct(t, rangeStart, rangeEndExclusive);
  }, [rangeStart, rangeEndExclusive]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 col-span-1 xl:col-span-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          Subscription periods
        </p>
        <p className="text-[10px] text-slate-400">
          Paid invoice coverage · contract estimate when coverage is empty
        </p>
      </div>

      <div className="flex flex-wrap gap-4 mb-3 text-[10px] text-slate-500">
        {Object.entries(KIND_STYLES).map(([kind, style]) => (
          <span key={kind} className="inline-flex items-center gap-1.5">
            <span className={`w-3 h-2 rounded-sm ${style.bar.split(' ')[0]}`} />
            {style.label}
          </span>
        ))}
        {todayPct != null && (
          <span className="inline-flex items-center gap-1.5">
            <span className="w-4 h-0 border-t-2 border-dashed border-rose-400" />
            Today
          </span>
        )}
      </div>

      <div
        className="border border-slate-100 rounded-lg overflow-auto bg-white"
        style={{ maxHeight: CHART_MAX_H }}
        onMouseLeave={() => setTooltip(null)}
      >
        <div style={{ minWidth: LABEL_W + TIMELINE_MIN_W }}>
          <div className="flex border-b border-slate-100 bg-slate-50/80 sticky top-0 z-20">
            <div
              className="sticky left-0 z-30 shrink-0 border-r border-slate-100 bg-slate-50/95"
              style={{ width: LABEL_W, height: AXIS_H }}
            />
            <div
              className="relative flex-1"
              style={{ minWidth: TIMELINE_MIN_W, height: AXIS_H }}
            >
              {monthTicks.map(tick => (
                <span
                  key={tick.ms}
                  className="absolute top-1 text-[9px] text-slate-400 -translate-x-1/2 whitespace-nowrap"
                  style={{ left: `${msToPct(tick.ms, rangeStart, rangeEndExclusive)}%` }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>

          {rows.map(row => (
            <div key={row.subscriptionId} className="flex border-b border-slate-50">
              <div
                className="sticky left-0 z-10 shrink-0 flex items-center px-2 border-r border-slate-100 bg-slate-50/95 text-[11px] font-medium text-slate-700"
                style={{ width: LABEL_W, height: ROW_H }}
                title={row.name}
              >
                <span className="truncate">{truncateName(row.name)}</span>
              </div>
              <div
                className="relative flex-1"
                style={{ minWidth: TIMELINE_MIN_W, height: ROW_H }}
              >
                {todayPct != null && (
                  <div
                    className="absolute top-0 bottom-0 w-px bg-rose-400/80 z-[1] pointer-events-none"
                    style={{ left: `${todayPct}%` }}
                  />
                )}
                {row.segments.map((seg, idx) => {
                  const left = msToPct(seg.start.getTime(), rangeStart, rangeEndExclusive);
                  const right = msToPct(
                    seg.endExclusive.getTime(),
                    rangeStart,
                    rangeEndExclusive,
                  );
                  const width = Math.max(0.4, right - left);
                  const barStyle = KIND_STYLES[seg.kind];
                  return (
                    <div
                      key={`${row.subscriptionId}-${idx}`}
                      role="presentation"
                      className={`absolute top-2 bottom-2 rounded-md ${barStyle.bar} shadow-sm cursor-default transition-colors`}
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                        minWidth: 4,
                      }}
                      onMouseEnter={e => {
                        setTooltip({
                          seg,
                          subName: row.name,
                          x: e.clientX,
                          y: e.clientY,
                        });
                      }}
                      onMouseMove={e => {
                        setTooltip(prev =>
                          prev ? { ...prev, x: e.clientX, y: e.clientY } : null,
                        );
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {tooltip && (
        <div
          className="fixed z-[100] pointer-events-none px-2.5 py-2 rounded-lg bg-slate-900 text-white text-xs shadow-lg max-w-[260px]"
          style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
        >
          <p className="font-semibold truncate">{tooltip.subName}</p>
          <p className="text-slate-300 mt-0.5">{formatSegmentRange(tooltip.seg)}</p>
          <p className="text-slate-400 text-[10px] mt-1">
            {KIND_STYLES[tooltip.seg.kind].label}
            {tooltip.seg.detail ? ` · ${tooltip.seg.detail}` : ''}
          </p>
        </div>
      )}
    </div>
  );
};

